import { safe, ErrorADT } from "pb.safe";
import { z } from "zod";

import type { Route } from "../app/routes/shared/route";
import { logger } from "../shared/logger";
import { auth0Config, TokenResponseSchema } from "./shared/auth0";

const REFRESH_TIMEOUT = 15_000;

const Auth0ErrorSchema = z.object({
	error: z.string(),
	error_description: z.string().optional(),
});

// auth0 errors meaning the refresh token itself is dead, so retrying is futile
const REJECTED_ERRORS = new Set([
	"invalid_grant",
	"invalid_client",
	"unauthorized_client",
]);

// one log message per status so each failure mode can be searched and alerted on
const STATUS_MESSAGES: Record<number, string> = {
	400: "refresh tokens: auth0 bad request",
	401: "refresh tokens: auth0 unauthorised",
	403: "refresh tokens: auth0 forbidden",
	429: "refresh tokens: auth0 rate limited",
	500: "refresh tokens: auth0 server error",
	502: "refresh tokens: auth0 bad gateway",
	503: "refresh tokens: auth0 service unavailable",
	504: "refresh tokens: auth0 gateway timeout",
};

/**
 * Refreshes configuration tokens.
 *
 * `RefreshTokenRejected` means auth0 refused the refresh token and the user
 * must log in again. `RefreshTokenUnavailable` is transient (network, timeout,
 * auth0 5xx / 429, malformed body) and the tokens should be kept.
 */
export async function refreshTokens(
	tokens: Partial<Route.Tokens> | undefined,
): Promise<
	| Route.Tokens
	| ErrorADT<
			| "TokensInvalid"
			| "AccessTokenInvalid"
			| "RefreshTokenRejected"
			| "RefreshTokenUnavailable"
	  >
> {
	const { authAccessToken, authRefreshToken, authIdToken } = tokens ?? {};

	if (!(authAccessToken && authRefreshToken && authIdToken)) {
		logger.warn("refresh tokens: invalid tokens configuration");

		return ErrorADT("TokensInvalid");
	}

	logger.info("refresh tokens: refreshing access token");

	// how long auth0 took to answer, independent of the device clock
	const startedAt = performance.now();
	const elapsedMs = () => Math.round(performance.now() - startedAt);

	const response = await safe(() =>
		fetch(`https://${auth0Config.domain}/oauth/token`, {
			method: "POST",
			body: new URLSearchParams({
				grant_type: "refresh_token",
				client_id: auth0Config.clientId,
				refresh_token: authRefreshToken,
			}),
			signal: AbortSignal.timeout(REFRESH_TIMEOUT),
		}),
	);

	const requestFailed = (error: Error) => {
		logger.error(
			error.name === "TimeoutError"
				? "refresh tokens: request timed out"
				: "refresh tokens: request failed",
			{
				"zeller.auth.errorName": error.name,
				"zeller.auth.errorMessage": error.message,
				"zeller.auth.elapsedMs": elapsedMs(),
			},
		);
		return ErrorADT("RefreshTokenUnavailable");
	};

	if (response instanceof Error) {
		return requestFailed(response);
	}

	const body: unknown = await safe(() => response.json());

	// the timeout also covers the body, so a stalled body rejects here
	if (body instanceof Error && body.name !== "SyntaxError") {
		return requestFailed(body);
	}

	if (!response.ok) {
		const auth0Error = Auth0ErrorSchema.safeParse(body);
		const attributes = {
			"zeller.auth.status": response.status,
			"zeller.auth.error": auth0Error.data?.error,
			"zeller.auth.errorDescription": auth0Error.data?.error_description,
			"zeller.auth.retryAfter":
				response.headers.get("Retry-After") ?? undefined,
			"zeller.auth.elapsedMs": elapsedMs(),
		};

		if (auth0Error.success && REJECTED_ERRORS.has(auth0Error.data.error)) {
			logger.error("refresh tokens: refresh token rejected", attributes);
			return ErrorADT("RefreshTokenRejected");
		}

		logger.error(
			STATUS_MESSAGES[response.status] ??
				"refresh tokens: auth0 error response",
			attributes,
		);
		return ErrorADT("RefreshTokenUnavailable");
	}

	const tokenResponse = TokenResponseSchema.safeParse(body);

	if (!tokenResponse.success) {
		// field names only, never values: the body may carry rotated tokens
		logger.error("refresh tokens: malformed token response", {
			"zeller.auth.status": response.status,
			// a SyntaxError message quotes the body, so log its name only
			"zeller.auth.errorName": body instanceof Error ? body.name : undefined,
			"zeller.auth.invalidFields": tokenResponse.error.issues
				.map((issue) => issue.path.join("."))
				.join(","),
			"zeller.auth.receivedFields":
				body && typeof body === "object" && !(body instanceof Error)
					? Object.keys(body)
							.sort((a, b) => a.localeCompare(b))
							.join(",")
					: "",
			"zeller.auth.elapsedMs": elapsedMs(),
		});
		return ErrorADT("RefreshTokenUnavailable");
	}

	return {
		authAccessToken: tokenResponse.data.access_token,
		authRefreshToken: tokenResponse.data.refresh_token,
		authIdToken: tokenResponse.data.id_token,
	};
}
